import { useMemo, useState } from 'react'
import {
	type ComplexNumericMode,
	MiniTabbedWidgetWrapper,
	WidgetInputButtonGroup,
	WidgetInputComplexNumericField,
	WidgetInputLargeTextField,
	WidgetLabel,
	WidgetSelectGrid,
	WidgetSelectGroup,
	type WidgetSelectGroupItem,
} from '../../../../rapid-ui'
import type { Dimension, DimensionMode, Layer, ShapeFillMode, ShapeFit, ShapeKind, ShapePreset } from '../../data/v1'
import { BoxTabIcon, LayoutTabIcon, ShapeTabIcon } from '../../glyphs/tabs'
import { useSizeSetByParent } from '../../lib/parent-sizing'
import { SHAPE_PRESET_IDS, SHAPE_PRESETS } from '../../lib/shape-presets'
import { useDesignerActions } from '../../use-designer-actions'
import { BoxInspector } from './box'
import { ColorModeInspector } from './color-mode'
import { FloatControls } from './float-controls'
import { SetByParent } from './set-by-parent'

const dimensionModes: ComplexNumericMode<DimensionMode>[] = [
	{ id: 'px', label: 'px', numeric: true },
	{ id: '%', label: '%', numeric: true },
]

const SHAPE_KIND_ITEMS: WidgetSelectGroupItem<ShapeKind>[] = [
	{ id: 'rectangle', label: 'Rectangle', render: () => <span className="text-xs">Rectangle</span> },
	{ id: 'preset', label: 'Preset', render: () => <span className="text-xs">Preset</span> },
]

const shapeFitItems: WidgetSelectGroupItem<ShapeFit>[] = [
	{ id: 'contain', label: 'Contain', render: () => <span className="text-xs">Contain</span> },
	{ id: 'stretch', label: 'Stretch', render: () => <span className="text-xs">Stretch</span> },
]

function DimensionField(
	{ label, value, onChange }: { label: string; value: Dimension; onChange: (d: Dimension) => void },
) {
	const numericValue = value.mode === '%' ? value.percentValue : value.pxValue

	return (
		<WidgetLabel label={label}>
			<WidgetInputComplexNumericField
				value={numericValue}
				onChange={(v) => {
					const key = value.mode === '%' ? 'percentValue' : 'pxValue'
					onChange({ ...value, [key]: v })
				}}
				min={0}
				defaultValue={100}
				modes={dimensionModes}
				mode={value.mode}
				onModeChange={(mode) => onChange({ ...value, mode })}
			/>
		</WidgetLabel>
	)
}

function PresetPicker({ value, onChange }: { value: ShapePreset; onChange: (preset: ShapePreset) => void }) {
	return (
		<WidgetSelectGrid
			labels="tooltip"
			value={value}
			onChange={onChange}
			items={SHAPE_PRESET_IDS.map((id) => ({
				id,
				label: SHAPE_PRESETS[id].label,
				render: ({ className }) => (
					<div className={`${className} flex items-center justify-center bg-white text-grey-700`}>
						<svg viewBox={SHAPE_PRESETS[id].viewBox} className="size-7">
							<path d={SHAPE_PRESETS[id].path} fill="currentColor" fillRule="evenodd" />
						</svg>
					</div>
				),
			}))}
		/>
	)
}

export type ShapeLayerInspectorProps = {
	layer: Extract<Layer, { type: 'shape' }>
}

export function ShapeLayerInspector({ layer }: ShapeLayerInspectorProps) {
	const { updateLayer } = useDesignerActions()
	const [tab, setTab] = useState<'shape' | 'layout' | 'box'>('shape')
	const sizeSetByParent = useSizeSetByParent(layer.id)

	const setFillMode = (fillMode: ShapeFillMode) => updateLayer(layer.id, { fillMode })

	return (
		<>
			<FloatControls layer={layer} />
			<MiniTabbedWidgetWrapper
				value={tab}
				onChange={setTab}
				tabs={[
					{
						id: 'shape',
						icon: <ShapeTabIcon />,
						label: 'Shape',
						children: (
							<div className="flex flex-col gap-2">
								<WidgetLabel label="Kind">
									<WidgetSelectGroup
										value={layer.shapeKind}
										onChange={(shapeKind) => updateLayer(layer.id, { shapeKind })}
										items={SHAPE_KIND_ITEMS}
									/>
								</WidgetLabel>

								{layer.shapeKind === 'preset' && (
									<PresetPicker
										value={layer.preset}
										onChange={(preset) => updateLayer(layer.id, { preset })}
									/>
								)}

								<ColorModeInspector
									label="Fill"
									value={layer.fill}
									onChange={(fill) => updateLayer(layer.id, { fill })}
									optIn={{
										optedIn: layer.fillMode === 'value',
										onOptIn: () => setFillMode('value'),
										onOptOut: () => setFillMode('original'),
									}}
								/>
							</div>
						),
					},
					{
						id: 'layout',
						icon: <LayoutTabIcon />,
						label: 'Layout',
						children: (
							<div className="flex flex-col gap-2">
								<SetByParent isSet={sizeSetByParent.width}>
									<DimensionField
										label="Width"
										value={layer.width}
										onChange={(width) => updateLayer(layer.id, { width })}
									/>
								</SetByParent>
								<SetByParent isSet={sizeSetByParent.height}>
									<DimensionField
										label="Height"
										value={layer.height}
										onChange={(height) => updateLayer(layer.id, { height })}
									/>
								</SetByParent>
								{layer.shapeKind !== 'rectangle' && (
									<WidgetLabel label="Fit">
										<WidgetSelectGroup
											value={layer.shapeFit}
											onChange={(shapeFit) => updateLayer(layer.id, { shapeFit })}
											items={shapeFitItems}
										/>
									</WidgetLabel>
								)}
							</div>
						),
					},
					{
						id: 'box',
						icon: <BoxTabIcon />,
						label: 'Box',
						children: (
							<BoxInspector
								layerId={layer.id}
								background={layer.background}
								onBackgroundChange={(updates, options) =>
									updateLayer(layer.id, (prev) => ({
										background: { ...(prev as typeof layer).background, ...updates },
									}), options)}
								borderRadius={layer.borderRadius}
								onBorderRadiusChange={(borderRadius) => updateLayer(layer.id, { borderRadius })}
								borderRadiusCorners={layer.borderRadiusCorners}
								onBorderRadiusCornersChange={(borderRadiusCorners) =>
									updateLayer(layer.id, { borderRadiusCorners })}
								spacing={layer.spacing}
								onSpacingChange={(spacing) => updateLayer(layer.id, { spacing })}
								onTransientSpacingChange={(spacing) =>
									updateLayer(layer.id, { spacing }, { transient: true })}
							/>
						),
					},
				]}
			/>
		</>
	)
}
